/**
 * useCompetitionData — every competition listener, and everything derived from
 * what they load.
 *
 * This is the read half of `useCompetition`: the competition list, the
 * per-user registration and proxy-guest maps, the always-on active-competition
 * listeners the map tab reads, the on-demand detail listeners for
 * `activeCompId`, and the ranking functions built on top of them. Nothing
 * here writes.
 *
 * `activeCompId` comes from the facade because it is public — callers set it.
 * The attempts scope does not: it exists only to shape the detail listener, so
 * it lives here with the state it shapes.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { useListenerRetry } from "../useListenerRetry";
import { activeStandardCompetitions, findActiveStandardCompetition } from "../../competitionUtils";
import { composeGroupRanking } from "@sendly/shared";
import { subscribeJudgeQueue } from "../../utils/judgeQueue";
import { competitionsListQuery } from "../../utils/competitionFetch";

// Slightly longer than `useGymData`'s 10 s no-data timeout: the competition
// list subscribes behind the gym data, so its first snapshot legitimately
// lands later.
const LOAD_TIMEOUT_MS = 12000;
/** Stable identity so a scoped-but-empty attempts list doesn't churn memos. */
const EMPTY_ATTEMPTS = [];
/**
 * Optimistic merge: pending JAM saves from the durable queue (op === "jam",
 * matching `compId`) override or supplement a server-loaded attempts array.
 * The competitor sees the snapshot they just saved even if the queue hasn't
 * been able to flush it to Firestore yet (offline / flaky network). Pending
 * always wins over the server doc because the user's intent is the latest
 * local snapshot.
 */
function mergePendingJam(attempts, pendingQueueItems, compId) {
  if (!compId || !attempts) return attempts;
  const myCompJam = pendingQueueItems.filter(
    (it) => it && it.op === "jam" && it.payload?.compId === compId
  );
  if (myCompJam.length === 0) return attempts;
  const byId = new Map();
  for (const a of attempts) {
    byId.set(`${a.uid}_${a.routeId}`, a);
  }
  // Process oldest pending JAM first so the latest save wins (last-write-wins).
  myCompJam.sort((a, b) => (a.enqueuedAt || 0) - (b.enqueuedAt || 0));
  for (const it of myCompJam) {
    const p = it.payload;
    const s = p.snapshot || {};
    const aid = p.attemptId || `${p.uid}_${p.routeId}`;
    const existing = byId.get(aid) || {};
    byId.set(aid, {
      ...existing,
      uid: p.uid,
      routeId: p.routeId,
      groupId: p.groupId || existing.groupId || "",
      sessionId: p.sessionId || existing.sessionId || "",
      zoneAttempts: s.zoneAttempts || 0,
      zoneReached: !!s.zoneReached,
      topAttempts: s.topAttempts || 0,
      topped: !!s.topped,
      registeredBy: p.actorUid || existing.registeredBy || p.uid,
      _pendingJam: true,
    });
  }
  return Array.from(byId.values());
}

/**
 * @param {object} p
 * @param {boolean} [p.listDrafts]  the viewer holds `competitions.manage` (or is a
 *   platform admin): list unfiltered, drafts included. Anyone else gets the
 *   announced competitions only — the rules refuse them an unfiltered list.
 */
export function useCompetitionData({ authUser, gymId, activeCompId, listDrafts = false }) {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Set when the competition list never arrived and the reattach budget ran
  // out, so the tab can offer a retry instead of an eternal "Loading…".
  const [loadFailed, setLoadFailed] = useState(false);

  // Per-competition detail data (loaded on demand)
  const [groups, setGroups] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  // Which of the three row listeners above have delivered their FIRST snapshot
  // for `compId` (or failed). The row states start empty and keep the previous
  // competition's rows until the new snapshot lands, so an empty list is NOT
  // "this competition has no rows" — a surface that counts or prints them has
  // to wait for `detailStatus === "ready"` (see below).
  const [detailSync, setDetailSync] = useState({ compId: null, groups: false, sessions: false, registrations: false, failed: false });

  // ── Attempts listener scope ──
  //
  // `null` = the whole subcollection. Two narrowed shapes:
  //
  //   { uid, groupId }  — a plain registered competitor. Their route cards read
  //                       their own rows and CompetitionView renders only THEIR
  //                       group's leaderboard, so that is all they get.
  //   { groupIds: [] }  — a judge. `CompetitionJudgeModal` already lists only
  //                       competitors in the judge's assigned sessions, so the
  //                       groups behind those sessions are everything the judge
  //                       can render. An empty array means "nothing yet"
  //                       (registrations still loading) and opens no listener —
  //                       it must NOT fall back to the whole collection.
  //
  // The unrestricted listener is what a 250-competitor × 50-route competition
  // cannot afford — 12 500 docs streamed to a phone, re-downloaded on every
  // reload (Firestore is memory-only here), plus a snapshot event and a full
  // array rebuild for every tap anyone makes anywhere in the hall. The judges'
  // phones were on that path, and they are the ones that have to stay
  // responsive.
  const [attemptsScope, setAttemptsScopeRaw] = useState(null);
  const setAttemptsScope = useCallback((scope) => {
    const uid = scope?.uid || null;
    const groupId = scope?.groupId || null;
    const groupIds = Array.isArray(scope?.groupIds) ? [...scope.groupIds].filter(Boolean).sort() : null;
    setAttemptsScopeRaw(prev => {
      if (groupIds) {
        if (prev?.groupIdsKey === groupIds.join(",")) return prev;
        return { groupIds, groupIdsKey: groupIds.join(","), uid: null, groupId: null };
      }
      if (!prev?.groupIds && (prev?.uid || null) === uid && (prev?.groupId || null) === groupId) return prev;
      return (uid && groupId) ? { uid, groupId, groupIds: null, groupIdsKey: null } : null;
    });
  }, []);
  const scopeUid = attemptsScope?.uid || null;
  const scopeGroupId = attemptsScope?.groupId || null;
  const scopeGroupIdsKey = attemptsScope?.groupIdsKey ?? null;
  const attemptsNarrowed = !!(scopeUid && scopeGroupId) || scopeGroupIdsKey !== null;

  // `attemptsAll` for the unrestricted listener; `attemptsSources` for a scoped
  // one, which can have several queries behind it (own rows + group, or one per
  // judged group) merged by document id.
  const [attemptsAll, setAttemptsAll] = useState([]);
  const [attemptsSources, setAttemptsSources] = useState({});
  const attempts = useMemo(() => {
    if (!attemptsNarrowed) return attemptsAll;
    const keys = Object.keys(attemptsSources);
    if (keys.length === 0) return EMPTY_ATTEMPTS;
    if (keys.length === 1) return attemptsSources[keys[0]];
    const byId = new Map();
    for (const key of keys) {
      for (const a of attemptsSources[key]) byId.set(a.id, a);
    }
    return [...byId.values()];
  }, [attemptsNarrowed, attemptsAll, attemptsSources]);
  // Pending JAM saves from the durable queue — used to optimistically reflect
  // offline self-saves in the UI before the Firestore write has acknowledged.
  // Without this merge, a competitor saving 4 attempts offline on route 7
  // would re-open the route and see "0 attempts" until the network returns
  // and the aggregate write completes.
  const [pendingQueueItems, setPendingQueueItems] = useState([]);
  useEffect(() => {
    const unsub = subscribeJudgeQueue(setPendingQueueItems);
    return unsub;
  }, []);

  // Track current user's registrations across all active competitions (for list view)
  const [myRegistrationMap, setMyRegistrationMap] = useState({});
  // Track proxy-registered guests for the current user across active competitions
  const [myProxyGuests, setMyProxyGuests] = useState({});

  // ── Listener resilience ──
  //
  // Every listener in this hook used to run without an error callback and
  // without a timeout, which made one failure mode invisible and unrecoverable:
  // a wedged long-polling channel leaves the subscription attached but silent,
  // so `loading` never clears and the competition tab shows "Loading…" forever.
  // On a phone that is not a hypothetical — it is the congested-hall case, and
  // the only escape was for the competitor to know about pull-to-refresh.
  const {
    nonce: reattachNonce, onError: onListenerError, markAlive,
    requestRetry: requestReattach, reset: resetReattach,
  } = useListenerRetry("useCompetition", `${gymId || ""}|${authUser?.uid || ""}`);

  /** Manual retry from the tab's failure screen. */
  const retryLoad = useCallback(() => {
    setLoadFailed(false);
    setLoading(true);
    resetReattach();
  }, [resetReattach]);

  // No-data timeout. A silent channel produces no error to react to, so the
  // only signal is the absence of a first snapshot.
  useEffect(() => {
    if (!loading || !gymId) return undefined;
    const timeout = setTimeout(() => {
      if (requestReattach(300)) return;
      setLoading(false);
      setLoadFailed(true);
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [loading, gymId, requestReattach]);

  // Competition list listener — use docChanges to avoid unnecessary re-renders.
  // `listDrafts` is a dependency: a manager's member document often resolves
  // after the first render, so the listener starts filtered and is re-opened
  // unfiltered once the cap is known (and back, if it is revoked).
  useEffect(() => {
    if (!gymId) { setLoading(false); return; }
    let initialized = false;
    const unsub = onSnapshot(competitionsListQuery(gymId, { includeDrafts: listDrafts }), snap => {
      if (initialized && snap.docChanges().length === 0) return;
      initialized = true;
      markAlive();
      setCompetitions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
      setLoadFailed(false);
    }, onListenerError("competitions"));
    return unsub;
  }, [gymId, listDrafts, reattachNonce, onListenerError, markAlive]);

  // Stable active comp IDs to avoid listener churn when competitions reference changes.
  // Standard competitions only: a challenge has no registrations, and an
  // external one has no subcollections at all — a per-user registration
  // listener on either would watch a document that can never exist.
  const activeCompIds = useMemo(() =>
    activeStandardCompetitions(competitions).map(c => c.id).sort().join(","),
    [competitions]
  );

  // Per-user registration listeners for active competitions (lightweight, 1 doc per comp)
  useEffect(() => {
    if (!gymId || !authUser?.uid || !activeCompIds) { setMyRegistrationMap({}); return; }
    const ids = activeCompIds.split(",").filter(Boolean);
    if (ids.length === 0) { setMyRegistrationMap({}); return; }
    const unsubs = ids.map(compId => {
      const regRef = doc(db, "gyms", gymId, "competitions", compId, "registrations", authUser.uid);
      return onSnapshot(regRef, snap => {
        setMyRegistrationMap(prev => {
          const val = snap.exists() ? { id: snap.id, ...snap.data() } : null;
          // Skip update if data hasn't changed (shallow check on key fields)
          const old = prev[compId] ?? null;
          // "Key absent" must mean "not loaded yet", never "no registration" —
          // callers that ACT on this map (the ?reg=1 deep link decides between
          // a new entry and an edit) cannot tell the two apart otherwise, and
          // would race the listener into opening a blank form for someone who
          // is already entered. So the first snapshot always plants the key,
          // even when it is null; later null snapshots still short-circuit.
          if (val === null && old === null) return compId in prev ? prev : { ...prev, [compId]: null };
          if (val === null || old === null) return { ...prev, [compId]: val };
          // No `billing.*` terms: the amounts left the document, and the fee
          // line they used to gate a re-render for is derived from the guest and
          // group listeners, which re-render on their own.
          if (old.role === val.role && old.groupId === val.groupId && old.sessionId === val.sessionId && old.isOrganizer === val.isOrganizer && old.entryFeePaid === val.entryFeePaid && old.startNumber === val.startNumber && old.paidAmount === val.paidAmount
            // The early-bird snapshot is a map: compare its fields, or a staff override would not refresh the card's fee row.
            && old.earlyBird?.tierId === val.earlyBird?.tierId && old.earlyBird?.discountPct === val.earlyBird?.discountPct && old.earlyBird?.source === val.earlyBird?.source) return prev;
          return { ...prev, [compId]: val };
        });
      }, onListenerError("myRegistration"));
    });
    return () => unsubs.forEach(u => u());
  }, [gymId, authUser?.uid, activeCompIds, reattachNonce, onListenerError]);

  // Per-user proxy guest listeners for active competitions
  useEffect(() => {
    if (!gymId || !authUser?.uid || !activeCompIds) { setMyProxyGuests({}); return; }
    const ids = activeCompIds.split(",").filter(Boolean);
    if (ids.length === 0) { setMyProxyGuests({}); return; }
    const unsubs = ids.map(compId => {
      const proxyQ = query(
        collection(db, "gyms", gymId, "competitions", compId, "registrations"),
        where("proxyUid", "==", authUser.uid)
      );
      return onSnapshot(proxyQ, snap => {
        setMyProxyGuests(prev => {
          const guests = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          const old = prev[compId];
          if (!old && guests.length === 0) return prev;
          return { ...prev, [compId]: guests };
        });
      }, onListenerError("myProxyGuests"));
    });
    return () => unsubs.forEach(u => u());
  }, [gymId, authUser?.uid, activeCompIds, reattachNonce, onListenerError]);

  // Track sessions for active competition (for map integration — independent of activeCompId).
  // `findActiveStandardCompetition`, not an inline find: this one and the
  // groups listener below used to omit the external exclusion the other three
  // copies had, so an older active external comp (no `sessions` subcollection)
  // won the `find` and left the map without a scorecard. See competition/settings.js.
  const [activeCompSessions, setActiveCompSessions] = useState([]);
  useEffect(() => {
    const activeComp = findActiveStandardCompetition(competitions);
    if (!gymId || !activeComp) { setActiveCompSessions([]); return; }
    return onSnapshot(collection(db, `gyms/${gymId}/competitions/${activeComp.id}`, "sessions"), snap => {
      setActiveCompSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, onListenerError("activeCompSessions"));
  }, [gymId, activeCompIds, reattachNonce, onListenerError]);

  // Groups of the active competition, same always-on scope as the sessions
  // above. The map needs the competitor's group MODE: in a judged group the
  // judge records and the competitor must not self-score, and without the
  // group doc the map cannot tell the two apart. A handful of docs, live only
  // while a competition is active.
  const [activeCompGroups, setActiveCompGroups] = useState([]);
  useEffect(() => {
    const activeComp = findActiveStandardCompetition(competitions);
    if (!gymId || !activeComp) { setActiveCompGroups([]); return; }
    return onSnapshot(collection(db, `gyms/${gymId}/competitions/${activeComp.id}`, "groups"), snap => {
      setActiveCompGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, onListenerError("activeCompGroups"));
  }, [gymId, activeCompIds, reattachNonce, onListenerError]);

  // The competitor's OWN attempt rows in the active competition — same
  // always-on scope as the sessions and groups above, and for the same reason:
  // the map has to answer "does this route already have my score?" at TAP
  // time. It used to answer with a `getDoc` fired from an effect after the tap,
  // and that one round trip is what made the ordinary RouteModal flash first —
  // and, when the request stalled on a congested hall network, stay up with the
  // scorecard never arriving. One `uid ==` query per entered competitor, at
  // most one doc per route on their scorecard.
  //
  // Competitor-only: a judge's map tap opens the judge editor, which reads
  // nothing from here, and only competitors own attempt rows.
  //
  // An id string, not the competition object, so the listener doesn't churn
  // when `competitions` or `myRegistrationMap` merely change identity.
  const myAttemptsCompId = useMemo(() => {
    const active = findActiveStandardCompetition(competitions);
    if (!active || !authUser?.uid) return null;
    return myRegistrationMap[active.id]?.role === "competitor" ? active.id : null;
  }, [competitions, myRegistrationMap, authUser?.uid]);

  // `null` = the first snapshot is not in yet, which is NOT the same as "no
  // attempts": a scorecard opened on a missing row shows 0/0 and would
  // overwrite a real score on save, so callers must wait rather than assume.
  const [myAttemptsRaw, setMyAttemptsRaw] = useState(null);
  useEffect(() => {
    if (!gymId || !myAttemptsCompId || !authUser?.uid) { setMyAttemptsRaw(null); return; }
    const myAttemptsQuery = query(
      collection(db, `gyms/${gymId}/competitions/${myAttemptsCompId}`, "attempts"),
      where("uid", "==", authUser.uid)
    );
    return onSnapshot(myAttemptsQuery, snap => {
      setMyAttemptsRaw(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, onListenerError("activeCompMyAttempts"));
  }, [gymId, myAttemptsCompId, authUser?.uid, reattachNonce, onListenerError]);

  // Same optimistic pending-JAM merge the Competition tab's list gets, so a
  // save the queue hasn't flushed yet is still there when the route is
  // reopened from the map.
  const activeCompMyAttempts = useMemo(
    () => mergePendingJam(myAttemptsRaw, pendingQueueItems, myAttemptsCompId),
    [myAttemptsRaw, pendingQueueItems, myAttemptsCompId]
  );

  // Detail listeners for activeCompId — use docChanges to skip no-op updates
  useEffect(() => {
    if (!gymId || !activeCompId) {
      setGroups([]); setSessions([]); setRegistrations([]);
      setAttemptsAll([]); setAttemptsSources({});
      setDetailSync(prev => prev.compId === null ? prev : { compId: null, groups: false, sessions: false, registrations: false, failed: false });
      return;
    }
    // A re-run for the SAME competition (a changed attempts scope, a reattach)
    // keeps what already arrived; a different competition starts from nothing.
    setDetailSync(prev => prev.compId === activeCompId
      ? prev
      : { compId: activeCompId, groups: false, sessions: false, registrations: false, failed: false });
    const basePath = `gyms/${gymId}/competitions/${activeCompId}`;
    const makeListener = (ref, setter, label, syncKey = null) => {
      let initialized = false;
      const reportError = onListenerError(label);
      // A synced listener ("did this competition's rows arrive?") also hears
      // metadata-only snapshots: the SDK may answer a new query first FROM THE
      // CACHE with whatever matching documents other listeners already hold
      // (e.g. the viewer's own registration), and the server's confirmation of
      // an unchanged result is a metadata-only change. Only a server snapshot
      // (`fromCache: false`) counts as the answer — the overview counted and
      // printed a one-row cache answer as the whole field (walkthrough V24).
      const options = syncKey ? { includeMetadataChanges: true } : {};
      return onSnapshot(ref, options, snap => {
        if (syncKey && !snap.metadata?.fromCache) {
          setDetailSync(prev => (prev.compId === activeCompId && !prev[syncKey]) ? { ...prev, [syncKey]: true } : prev);
        }
        // Metadata-only changes (the server confirmation, a pending write's
        // acknowledgement) never re-set the rows: same skip as before.
        if (initialized && snap.docChanges({ includeMetadataChanges: false }).length === 0) return;
        initialized = true;
        markAlive();
        setter(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => {
        reportError(err);
        // A failed read settles the status too: a surface waiting on it must
        // not wait forever (it falls back to what the props hold).
        if (syncKey) setDetailSync(prev => prev.compId === activeCompId ? { ...prev, failed: true } : prev);
      });
    };
    /** One attempts query feeding one slot of the merged scoped result. */
    const makeAttemptsSource = (q, sourceKey) => makeListener(
      q,
      (rows) => setAttemptsSources(prev => ({ ...prev, [sourceKey]: rows })),
      `attempts(${sourceKey})`,
    );
    const attemptsCol = collection(db, basePath, "attempts");
    const unsubs = [
      makeListener(collection(db, basePath, "groups"), setGroups, "groups", "groups"),
      makeListener(collection(db, basePath, "sessions"), setSessions, "sessions", "sessions"),
      makeListener(collection(db, basePath, "registrations"), setRegistrations, "registrations", "registrations"),
    ];
    if (attemptsNarrowed) {
      setAttemptsAll([]);
      setAttemptsSources({});
      const judgeGroupIds = scopeGroupIdsKey ? scopeGroupIdsKey.split(",").filter(Boolean) : null;
      if (judgeGroupIds) {
        // Judge: one query per judged group. `in` would be one query, but it
        // caps at 30 values and gives no per-group failure isolation; a judge
        // covers a handful of groups, so plain equality queries are simpler.
        // An empty list means the registrations haven't loaded yet — open
        // nothing rather than silently widening back to the whole collection.
        for (const gid of judgeGroupIds) {
          unsubs.push(makeAttemptsSource(query(attemptsCol, where("groupId", "==", gid)), `g:${gid}`));
        }
      } else {
        // Competitor: two queries rather than one. The group query feeds the
        // leaderboard; the uid query guarantees the competitor's OWN rows are
        // always there, since `attempt.groupId` is denormalized and a row that
        // never got migrated would otherwise vanish from their own scorecard.
        unsubs.push(makeAttemptsSource(query(attemptsCol, where("uid", "==", scopeUid)), "own"));
        unsubs.push(makeAttemptsSource(query(attemptsCol, where("groupId", "==", scopeGroupId)), "group"));
      }
    } else {
      setAttemptsSources({});
      unsubs.push(makeListener(attemptsCol, setAttemptsAll, "attempts(all)"));
    }
    return () => unsubs.forEach(u => u());
  }, [gymId, activeCompId, attemptsNarrowed, scopeUid, scopeGroupId, scopeGroupIdsKey, reattachNonce, onListenerError, markAlive]);

  // ── Derived data ──

  const myRegistration = useMemo(() => {
    if (!authUser || !activeCompId) return null;
    return registrations.find(r => r.uid === authUser.uid) || null;
  }, [authUser, activeCompId, registrations]);

  /**
   * Competitors registered with `excludeFromRanking` — scored normally, but
   * shown with a would-be rank instead of a real placement, and never pushing
   * anyone else down. The close-the-competition path has always built this
   * (see `excludedUidSet` in `closeCompetition`); the live paths below did not,
   * so the live board and the frozen result disagreed for the whole event.
   */
  const excludedUids = useMemo(() => new Set(
    registrations.filter(r => r.role === "competitor" && r.excludeFromRanking === true).map(r => r.uid)
  ), [registrations]);

  const getRankings = useCallback((groupId, finalsInfo) => {
    const comp = competitions.find(c => c.id === activeCompId);
    if (!comp) return [];

    // Finals group: filter to finals session attempts and finalist UIDs only
    if (finalsInfo) {
      const finalsSessionIdSet = new Set(finalsInfo.finalsSessionIds || []);
      const finalsGroupId = finalsInfo.sourceGroupId || groupId;
      return composeGroupRanking({
        settings: comp.settings,
        routePointsGroupId: finalsGroupId,
        competitorUids: finalsInfo.finalistUids || [],
        attempts: attempts.filter(a => a.groupId === finalsGroupId && finalsSessionIdSet.has(a.sessionId)),
        payerRegs: registrations,
        nameRegs: registrations.filter(r => r.groupId === finalsGroupId),
        excludedUids,
      });
    }

    const groupRegs = registrations.filter(r => r.groupId === groupId && r.role === "competitor");
    return composeGroupRanking({
      settings: comp.settings,
      routePointsGroupId: groupId,
      competitorUids: groupRegs.map(r => r.uid),
      attempts: attempts.filter(a => a.groupId === groupId),
      // Every registration, because a proxy guest's fee is paid by their
      // parent, who may sit in another group entirely.
      payerRegs: registrations,
      nameRegs: groupRegs,
      excludedUids,
    });
  }, [activeCompId, competitions, registrations, attempts, excludedUids]);

  /** Generate finalists from current rankings of given group(s).
   *  @param {string|string[]} groupIds - single groupId or array
   *  @param {number} topN - how many finalists to pick
   *  @param {string|null} sourceSessionId - optional: only count attempts from this session
   *  Returns top N UIDs (by score desc). */
  const generateFinalists = useCallback((groupIds, topN, sourceSessionId) => {
    if (!groupIds) return [];
    const ids = Array.isArray(groupIds) ? groupIds : [groupIds];
    if (ids.length === 0) return [];

    // If no session filter, use existing getRankings
    if (!sourceSessionId) {
      let merged = [];
      for (const gid of ids) {
        merged = merged.concat(getRankings(gid));
      }
      const best = {};
      for (const r of merged) {
        if (r.excluded) continue; // a would-be rank is not a qualifying place
        if (!best[r.uid] || r.score > best[r.uid].score) best[r.uid] = r;
      }
      const sorted = Object.values(best).sort((a, b) => b.score - a.score);
      const cutoffRank = sorted.length >= topN ? sorted[topN - 1]?.rank : null;
      return sorted.filter((r, i) => i < topN || (cutoffRank && r.rank === cutoffRank)).map(r => r.uid);
    }

    // Session-scoped ranking: filter attempts by sessionId
    const comp = competitions.find(c => c.id === activeCompId);
    if (!comp) return [];
    let merged = [];
    for (const gid of ids) {
      const groupRegs = registrations.filter(r => r.groupId === gid && r.role === "competitor");
      merged = merged.concat(composeGroupRanking({
        settings: comp.settings,
        routePointsGroupId: gid,
        competitorUids: groupRegs.map(r => r.uid),
        attempts: attempts.filter(a => a.groupId === gid && a.sessionId === sourceSessionId),
        payerRegs: registrations,
        nameRegs: groupRegs,
        excludedUids,
      }));
    }
    const best = {};
    for (const r of merged) {
      if (r.excluded) continue; // a would-be rank is not a qualifying place
      if (!best[r.uid] || r.score > best[r.uid].score) best[r.uid] = r;
    }
    const sorted = Object.values(best).sort((a, b) => b.score - a.score);
    const cutoffRank = sorted.length >= topN ? sorted[topN - 1]?.rank : null;
    return sorted.filter((r, i) => i < topN || (cutoffRank && r.rank === cutoffRank)).map(r => r.uid);
  }, [getRankings, activeCompId, competitions, registrations, attempts, excludedUids]);

  const effectiveAttempts = useMemo(
    () => mergePendingJam(attempts, pendingQueueItems, activeCompId),
    [attempts, pendingQueueItems, activeCompId]
  );

  // "idle" — no active competition; "loading" — the groups, sessions and
  // registrations of `activeCompId` have not all answered yet (the row arrays
  // may still hold another competition's rows or nothing); "ready" — all three
  // answered; "failed" — one of them errored. Derived against `activeCompId`
  // in the same render, so the render right after `setActiveCompId(x)` already
  // reads "loading" rather than the previous competition's "ready".
  const detailStatus = !activeCompId ? "idle"
    : detailSync.compId !== activeCompId ? "loading"
    : detailSync.failed ? "failed"
    : detailSync.groups && detailSync.sessions && detailSync.registrations ? "ready"
    : "loading";

  return {
    competitions, loading, loadFailed, retryLoad,
    groups, sessions, registrations, detailStatus, attempts: effectiveAttempts,
    myRegistration, myRegistrationMap, myProxyGuests,
    activeCompSessions, activeCompGroups, activeCompMyAttempts,
    setAttemptsScope,
    getRankings, generateFinalists,
  };
}
